import React from "react";
import { useState, useMemo, useEffect } from "react";
import * as XLSX from 'xlsx';

import "./Grids.css";
import {
  FaSortAmountUpAlt,
  FaSortAmountDown,
  FaSort,
  FaPlusCircle,
  FaFileExcel,
  FaTimesCircle,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";

import { DateFormatter } from "../../data/helper";
import {
  Column,
  Table,
  useReactTable,
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";
import { Tooltip } from "@progress/kendo-react-tooltip";

import { RankingInfo, rankItem } from "@tanstack/match-sorter-utils";

import { getEnquiryTypes } from "../../data/interfaces";
import { Switch } from "@progress/kendo-react-inputs";
import { getGlobalSearchResults } from "../../data/globalSearchData";
import { GlobalSearchItem } from "../../types/globalSearch.types";
import dayjs from "dayjs";

declare module "@tanstack/table-core" {
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    numberRange: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Simple numberRange filter - not used in this grid but required by the interface
const numberRangeFilter: FilterFn<any> = (row, columnId, value) => {
  return true; // Always return true since we don't use number range filtering in this grid
};

function GlobalSearchGrid(props: any) {
  const dateFormatter = new DateFormatter();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const exportToExcel = () => {
    let date = new Date().toDateString();
    date = dateFormatter.format(date);
    // Create a new workbook
    const wb = XLSX.utils.book_new();
    
    // Convert the data to a worksheet
    const ws = XLSX.utils.json_to_sheet(props.onExportData);
    
    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(wb, ws, "Enquiries");
    
    // Write the workbook and trigger a download
    XLSX.writeFile(wb, `enquiries_${date}.xlsx`);
  };

  const clientColumns = useMemo<ColumnDef<GlobalSearchItem>[]>(
    () => [
      {
        accessorKey: "ref",
        header: "Ref",
        enableColumnFilter: false,
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: GlobalSearchItem } }) => {
          return (
            <span style={{ fontWeight: "bold" }}>
              {row.original.account}
            </span>
          );
        },
      },
      {
        accessorKey: "contact",
        header: "Contact",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "siteAddr",
        header: "Site",
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: GlobalSearchItem } }) => (
          <Tooltip anchorElement="target" position="top">
            <span
              title={row.original.address}
              className="overflow__container"
            >
              {row.original.address}
            </span>
          </Tooltip>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        footer: (props) => props.column.id,
        cell: ({ row }: { row: { original: GlobalSearchItem } }) => (
          <Tooltip anchorElement="target" position="top">
            <span
              title={row.original.description}
              className="overflow__container"
            >
              {row.original.description}
            </span>
          </Tooltip>
        ),
      },  
      {
        accessorKey: "createdDate",
        header: "Created Date",
        footer: (props: { column: { id: any } }) => props.column.id,
        cell: ({ row }: { row: { original: GlobalSearchItem } }) => {
          return (
            <span style={{ fontWeight: "bold" }}>
                {dayjs(row.original.createdDate).format("DD-MM-YYYY")}
            </span>
          );
        },
      },             

      {
        accessorKey: "email",
        header: "Email",
        footer: (props) => props.column.id,
      },
      {
        accessorKey: "phone",
        header: "Phone",
        footer: (props) => props.column.id,
      },      
      {
        accessorKey: "postCode",
        header: "Postcode",
        footer: (props) => props.column.id,
      }, 
      {
        accessorKey: "type",
        header: "Type",
        footer: (props) => props.column.id,
      },           
    ],
    []
  );

  const [data, setData] = useState<GlobalSearchItem[]>([]);
  console.log("data", data)

//   useEffect(() => {
//     setData(props.onData);
//   }, [props.onData]);

  const table = useReactTable({
    data,
    columns: clientColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
      numberRange: numberRangeFilter,
    },
    state: {
      columnFilters,
      globalFilter,
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const [showDead, setShowDead] = useState(false);
  const handleShowDead = (event: any) => {
    setShowDead(event.target.value);
    props.onShowDead(event.target.value);
  };

  const [showProgressed, setShowProgressed] = useState(false);
  const handleShowProgressed = (event: any) => {
    setShowProgressed(event.target.value);
    props.onShowProgressed(event.target.value);
  };


  const searchGlobally = (query: string) => {
    if(query) {
        setLoading(true)
        setTimeout(() => {
          getGlobalSearchResults(query)
            .then((res) => {
                if(res) {
                    setData(res)
                }
                setLoading(false)
            })
            .catch((err) => {
              setLoading(false)
              console.log(err);
            });
        }, 1000);
    }
  };

  return (
    <div className="p-2">
      {/* This is the global column search box */}
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}>
            <div>Global Search</div>
            {/* <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowDead}
                defaultChecked={false}
              />
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaTimesCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.7rem",
                    color: showDead
                      ? "rgb(33, 67, 59)"
                      : "rgb(148 167 163)",
                  }}
                  title="Show Dead Enquiries?"
                />
              </Tooltip>
              <Switch
                className="grid__switch"
                size={"large"}
                onChange={handleShowProgressed}
                defaultChecked={false}
              />
              <Tooltip anchorElement="target" position="top" openDelay={0}>
                <FaCheckCircle
                  style={{
                    fontSize: "14pt",
                    marginLeft: "0.7rem",
                    color: showProgressed
                      ? "rgb(33, 67, 59)"
                      : "rgb(148 167 163)",
                  }}
                  title="Show Progressed Enquiries?"
                />
              </Tooltip> */}
            </div>            
            {/* <div style={{display: "flex", flexDirection: "row"}}>
              <div onClick={() => props.onAddToggle()} className="grid__addrecord__button">
                <FaPlusCircle style={{ fontSize: "14pt", marginRight: "1rem" }} />{" "}
                <p style={{ margin: 0 }}>Add new enquiry</p>
              </div>
              <div onClick={exportToExcel} className="grid__export__button">
                <FaFileExcel
                  style={{ fontSize: "14pt", marginRight: "1rem" }}
                />{" "}
                <p style={{ margin: 0 }}>Export enquiries</p>
              </div>
            </div> */}
          </div>
          <DebouncedInput
            value={globalFilter ?? ""}
            onChange={(value) => searchGlobally(value.toString())}
            placeholder="Global search..."
            className="filter__input__global"
          />
        </div>
      </div>

      <div className="h-2" />
      {
        loading ? (
          <div className="k-loader k-loader-sm k-loader-primary k-loader-spinner-3" style={{display: "flex"}} ><div style={{margin: "20px auto"}} className="k-loader-canvas"><span className="k-loader-segment"></span><span className="k-loader-segment"></span><span className="k-loader-segment"></span></div></div>

        ) : (
          ! data.length ? (
            <div style={{margin: "20px 0", textAlign: "center", color: "black", fontSize: "1rem"}}>Perform a search to see results</div>
          ) :
          <table className="grid__table">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <th key={header.id} colSpan={header.colSpan}>
                        {header.isPlaceholder ? null : (
                          <>
                            {/* This DIV is where you hover/click on the column header to sort */}
                            <div
                              {...{
                                className: header.column.getCanSort()
                                  ? "header__sortable"
                                  : "",
                                onClick: header.column.getToggleSortingHandler(),
                              }}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: (
                                  <FaSortAmountUpAlt style={{ fontSize: "14pt" }} />
                                ),
                                desc: (
                                  <FaSortAmountDown style={{ fontSize: "14pt" }} />
                                ),
                              }[header.column.getIsSorted() as string] ?? (
                                <FaSort style={{ fontSize: "14pt" }} />
                              )}
                            </div>

                            {header.column.getCanFilter() ? (
                              <div>
                                <Filter column={header.column} table={table} />
                              </div>
                            ) : null}
                          </>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => {
                const bgColor = () => {
                  if (row.original.deadDate) {
                    return "rgba(235, 199, 199, 0.5)";
                  } else if (row.original.progressed) {
                    return "rgba(215, 237, 215, 1)";
                  } else {
                    return "rgba(245, 249, 251, 1)";
                  }
                };

                return (
                  <tr
                    onDoubleClick={() => {
                      // props.onEditToggle(row.original.enquiryId)
                      props.onRowClick(row.original)

                    }}
                    style={{ backgroundColor: bgColor() }}
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <td key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )
      }

      {/* This is the pagination controls */}
      <div className="page__container">
        <div className="page__button__container">
          <button
            className="page__button"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            {"<<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            {"<"}
          </button>
          <button
            className="page__button"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            {">"}
          </button>
          <button
            className="page__button"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            {">>"}
          </button>
        </div>

        <div className="page__controls">
          <div className="page__numbers">
            <div>Page&nbsp;</div>
            <strong>
              {table.getState().pagination.pageIndex + 1} of{" "}
              {table.getPageCount()}
            </strong>
          </div>
          <div className="page__numbers__goto">
            <div className="page__numbers__gototext">| Go to page:</div>
            <input
              type="number"
              defaultValue={table.getState().pagination.pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                table.setPageIndex(page);
              }}
              className="filter__input"
            />
          </div>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
            className="page__select"
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}

function Filter({
  column,
  table,
}: {
  column: Column<any, unknown>;
  table: Table<any>;
}) {
  const firstValue = table
    .getPreFilteredRowModel()
    .flatRows[0]?.getValue(column.id);

  const columnFilterValue = column.getFilterValue();

  const sortedUniqueValues = useMemo(
    () =>
      typeof firstValue === "number"
        ? []
        : Array.from(column.getFacetedUniqueValues().keys()).sort(),
    [column.getFacetedUniqueValues()]
  );

  return typeof firstValue === "number" ? (
    <div>
      <div className="flex space-x-2">
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[0] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [value, old?.[1]])
          }
          placeholder={`Min ${
            column.getFacetedMinMaxValues()?.[0]
              ? `(${column.getFacetedMinMaxValues()?.[0]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
        <DebouncedInput
          type="number"
          min={Number(column.getFacetedMinMaxValues()?.[0] ?? "")}
          max={Number(column.getFacetedMinMaxValues()?.[1] ?? "")}
          value={(columnFilterValue as [number, number])?.[1] ?? ""}
          onChange={(value) =>
            column.setFilterValue((old: [number, number]) => [old?.[0], value])
          }
          placeholder={`Max ${
            column.getFacetedMinMaxValues()?.[1]
              ? `(${column.getFacetedMinMaxValues()?.[1]})`
              : ""
          }`}
          className="filter__input__minmax"
        />
      </div>
      <div className="h-1" />
    </div>
  ) : (
    <>
      <datalist id={column.id + "list"}>
        {sortedUniqueValues.slice(0, 5000).map((value: any) => (
          <option value={value} key={value} />
        ))}
      </datalist>
      <DebouncedInput
        type="text"
        value={(columnFilterValue ?? "") as string}
        onChange={(value) => column.setFilterValue(value)}
        placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
        className="filter__input"
        list={column.id + "list"}
      />
    </>
  );
}

// A debounced input react component
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange">) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <input
      {...props}
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

export default GlobalSearchGrid;
